/**
 * Agreements React Query Hooks
 *
 * Custom hooks for employee agreements management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { agreementsApi } from '../../lib/agreementsApi';
import type { AgreementUpdateInput } from '../../lib/evaluationsApi';

// Query keys
export const agreementKeys = {
  all: ['agreements'] as const,
  employee: (employeeId: number) => [...agreementKeys.all, 'employee', employeeId] as const,
  actionHolder: (employeeId: number) => [...agreementKeys.all, 'action-holder', employeeId] as const,
  team: () => [...agreementKeys.all, 'team'] as const,
  allOrganization: () => [...agreementKeys.all, 'organization'] as const,
  archived: () => [...agreementKeys.all, 'archived'] as const,
  detail: (agreementId: number) => [...agreementKeys.all, 'detail', agreementId] as const,
};

/**
 * Hook to get all agreements for an employee
 */
export function useEmployeeAgreements(employeeId: number) {
  return useQuery({
    queryKey: agreementKeys.employee(employeeId),
    queryFn: () => agreementsApi.getEmployeeAgreements(employeeId),
    enabled: employeeId > 0,
  });
}

/**
 * Hook to get action holder agreements for an employee
 * Returns open agreements where employee is responsible for follow-up
 */
export function useActionHolderAgreements(employeeId: number) {
  return useQuery({
    queryKey: agreementKeys.actionHolder(employeeId),
    queryFn: () => agreementsApi.getActionHolderAgreements(employeeId),
    enabled: employeeId > 0,
  });
}

/**
 * Hook to get team agreements for a manager
 */
export function useTeamAgreements() {
  return useQuery({
    queryKey: agreementKeys.team(),
    queryFn: () => agreementsApi.getTeamAgreements(),
  });
}

/**
 * Hook to get all open agreements in the organization (HR only)
 */
export function useAllOrganizationAgreements() {
  return useQuery({
    queryKey: agreementKeys.allOrganization(),
    queryFn: () => agreementsApi.getAllAgreements(),
  });
}

/**
 * Hook to get a single agreement with all updates
 */
export function useAgreementWithUpdates(agreementId: number) {
  return useQuery({
    queryKey: agreementKeys.detail(agreementId),
    queryFn: () => agreementsApi.getAgreementWithUpdates(agreementId),
    enabled: agreementId > 0,
  });
}

/**
 * Hook to add a progress update to an agreement
 */
export function useAddAgreementUpdate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agreementId, input }: { agreementId: number; input: AgreementUpdateInput }) =>
      agreementsApi.addUpdate(agreementId, input),
    onSuccess: (_, { agreementId }) => {
      // Invalidate the agreement detail query
      queryClient.invalidateQueries({ queryKey: agreementKeys.detail(agreementId) });
      // Also invalidate action holder and team queries as status might have changed
      queryClient.invalidateQueries({ queryKey: agreementKeys.all });
    },
  });
}

/**
 * Hook to get archived agreements (admin only)
 */
export function useArchivedAgreements() {
  return useQuery({
    queryKey: agreementKeys.archived(),
    queryFn: () => agreementsApi.getArchivedAgreements(),
  });
}

/**
 * Hook to delete (archive) an agreement
 */
export function useDeleteAgreement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (agreementId: number) => agreementsApi.deleteAgreement(agreementId),
    onSuccess: () => {
      // Invalidate all agreement queries
      queryClient.invalidateQueries({ queryKey: agreementKeys.all });
    },
  });
}

/**
 * Hook to update an agreement's description (only creator can edit when in concept)
 */
export function useUpdateAgreement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agreementId, beschrijving }: { agreementId: number; beschrijving: string }) =>
      agreementsApi.updateAgreement(agreementId, beschrijving),
    onSuccess: (_, { agreementId }) => {
      queryClient.invalidateQueries({ queryKey: agreementKeys.detail(agreementId) });
      queryClient.invalidateQueries({ queryKey: agreementKeys.all });
    },
  });
}

/**
 * Hook to update an agreement update's content (only author can edit)
 */
export function useUpdateAgreementUpdate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ updateId, content }: { updateId: number; content: string; agreementId: number }) =>
      agreementsApi.updateAgreementUpdate(updateId, content),
    onSuccess: (_, { agreementId }) => {
      queryClient.invalidateQueries({ queryKey: agreementKeys.detail(agreementId) });
    },
  });
}
